/**
 * Tesseract.js OCR extraction with structured text output.
 * Uses dynamic import to keep bundle impact minimal.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

// --- Type Definitions ---

export interface WordInfo {
  text: string;
  bbox: { x0: number; y0: number; x1: number; y1: number };
  confidence: number;
  lineIndex: number;
}

export interface StructuredTextResult {
  structuredText: WordInfo[];
  rawText: string;
  pageConfidence: number;
  error?: string;
}

const EMPTY_RESULT: StructuredTextResult = {
  structuredText: [],
  rawText: '',
  pageConfidence: 0,
};

/**
 * Extracts structured text from an image using Tesseract.js.
 *
 * Returns word-level data with bounding boxes and confidence scores,
 * organized by line index for spatial analysis on the backend.
 *
 * @param image - A data URL string, blob URL, or HTMLCanvasElement
 * @returns Structured text result with word-level detail
 */
export async function extractStructuredText(
  image: string | HTMLCanvasElement
): Promise<StructuredTextResult> {
  let worker: any = null;

  try {
    // Dynamic import — tesseract.js is an npm package but fetches WASM lazily
    const Tesseract = await import('tesseract.js');

    // Create worker with English language
    worker = await Tesseract.createWorker('eng', undefined, {
      logger: (m: any) => {
        if (m.status === 'recognizing text') {
          // Progress tracking can be hooked here if needed
        }
      },
    });

    // Convert canvas to data URL if needed
    let imageSource: string;
    if (image instanceof HTMLCanvasElement) {
      imageSource = image.toDataURL('image/png');
    } else {
      imageSource = image;
    }

    // Recognize with block-level structure enabled
    const result = await worker.recognize(imageSource, {}, { text: true, blocks: true });

    const data = result.data;
    const structuredText: WordInfo[] = [];
    let lineIndex = 0;

    // Traverse the Block > Paragraph > Line > Word hierarchy
    if (data.blocks) {
      for (const block of data.blocks) {
        if (!block.paragraphs) continue;
        for (const paragraph of block.paragraphs) {
          if (!paragraph.lines) continue;
          for (const line of paragraph.lines) {
            if (!line.words) continue;
            for (const word of line.words) {
              structuredText.push({
                text: word.text,
                bbox: {
                  x0: word.bbox.x0,
                  y0: word.bbox.y0,
                  x1: word.bbox.x1,
                  y1: word.bbox.y1,
                },
                confidence: word.confidence,
                lineIndex,
              });
            }
            lineIndex++;
          }
        }
      }
    }

    // Extract page-level confidence
    const pageConfidence = data.confidence ?? 0;

    // Raw text fallback
    const rawText = data.text || '';

    return {
      structuredText,
      rawText,
      pageConfidence,
    };
  } catch (error) {
    console.error('Tesseract.js extraction failed:', error);
    const message = error instanceof Error ? error.message : 'OCR extraction failed';
    return {
      ...EMPTY_RESULT,
      error: message,
    };
  } finally {
    // Always terminate the worker to free memory
    if (worker) {
      try {
        await worker.terminate();
      } catch {
        // Ignore termination errors
      }
    }
  }
}
